import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, MapPin, Mail, Globe, Phone, Check, X, Trash2, Store, User as UserIcon,
} from "lucide-react";

/**
 * AdminBusinessSubmissionsTab
 * -----------------------------
 * Community-nominated businesses that visitors submitted via the landing-page
 * "Submit A Business" dialog. Admins can approve (materialises a real
 * `businesses` row, marked source=community_submission) or reject.
 *
 * Endpoints:
 *   GET    /api/admin/business-submissions?status=pending
 *   POST   /api/admin/business-submissions/{id}/approve
 *   POST   /api/admin/business-submissions/{id}/reject
 *   DELETE /api/admin/business-submissions/{id}
 */
export default function AdminBusinessSubmissionsTab() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/business-submissions?status=${status}`,
        { withCredentials: true },
      );
      setItems(data?.submissions || []);
      setCounts(data?.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const { data } = await axios.post(
        `${API}/admin/business-submissions/${id}/approve`,
        {},
        { withCredentials: true },
      );
      toast.success(`Approved — created ${data.business_id}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (optional):", "") || "";
    setBusyId(id);
    try {
      await axios.post(
        `${API}/admin/business-submissions/${id}/reject`,
        { reason },
        { withCredentials: true },
      );
      toast.success("Marked as rejected");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    } finally {
      setBusyId(null);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    setBusyId(id);
    try {
      await axios.delete(
        `${API}/admin/business-submissions/${id}`,
        { withCredentials: true },
      );
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card data-testid="admin-business-submissions-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-tbc-navy flex items-center gap-2">
              <Store className="w-5 h-5 text-tbc-gold" />
              Community Submissions
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Businesses nominated by visitors via the &ldquo;Submit A Business&rdquo; form.
              Approving creates a directory listing (unverified, admin-owned)
              you can hand to the owner via the Claims flow.
            </p>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-900">
              {counts.pending} pending
            </span>
            <span className="px-2 py-1 rounded-full bg-green-100 text-green-900">
              {counts.approved} approved
            </span>
            <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-700">
              {counts.rejected} rejected
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="submissions-tab-pending">
              Pending ({counts.pending})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="submissions-tab-approved">
              Approved ({counts.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected" data-testid="submissions-tab-rejected">
              Rejected ({counts.rejected})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={status} className="mt-4">
            {loading ? (
              <div className="py-10 text-center text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto" />
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                No {status} submissions.
              </div>
            ) : (
              <ul className="space-y-3">
                {items.map((s) => (
                  <li
                    key={s.submission_id}
                    className="border border-gray-200 rounded-lg p-4 bg-white"
                    data-testid={`submission-row-${s.submission_id}`}
                  >
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start gap-2 flex-wrap">
                          <h3 className="font-heading text-lg text-tbc-navy">
                            {s.name}
                          </h3>
                          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 bg-tbc-cream text-tbc-navy rounded">
                            {s.category}
                            {s.custom_category ? ` · ${s.custom_category}` : ""}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {s.city}, {s.state}
                        </p>
                        {s.description && (
                          <p className="text-sm text-gray-700 mt-2">{s.description}</p>
                        )}
                        <div className="flex flex-wrap gap-3 mt-3 text-xs text-gray-600">
                          {s.website && (
                            <a
                              href={s.website.startsWith("http") ? s.website : `https://${s.website}`}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-1 hover:text-tbc-gold"
                            >
                              <Globe className="w-3 h-3" /> {s.website}
                            </a>
                          )}
                          {s.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {s.phone}
                            </span>
                          )}
                          {s.email && (
                            <a
                              href={`mailto:${s.email}`}
                              className="flex items-center gap-1 hover:text-tbc-gold"
                            >
                              <Mail className="w-3 h-3" /> {s.email}
                            </a>
                          )}
                        </div>

                        <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                          <div className="flex items-center gap-2">
                            <UserIcon className="w-3 h-3" />
                            <span>
                              Submitted by <strong className="text-gray-700">{s.submitter_name}</strong>
                              {" — "}
                              <a
                                href={`mailto:${s.submitter_email}`}
                                className="text-tbc-gold hover:underline"
                              >
                                {s.submitter_email}
                              </a>
                              {s.relationship && ` (${s.relationship})`}
                            </span>
                          </div>
                          {s.note && (
                            <div className="italic pl-5">&ldquo;{s.note}&rdquo;</div>
                          )}
                          <div className="pl-5 text-[11px] text-gray-400">
                            {new Date(s.created_at).toLocaleString()}
                          </div>
                          {s.rejection_reason && (
                            <div className="pl-5 text-red-600">
                              Rejection reason: {s.rejection_reason}
                            </div>
                          )}
                          {s.business_id && (
                            <div className="pl-5 text-green-700">
                              → Approved as {s.business_id}
                            </div>
                          )}
                        </div>
                      </div>

                      {status === "pending" && (
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => approve(s.submission_id)}
                            disabled={busyId === s.submission_id}
                            className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy"
                            data-testid={`approve-btn-${s.submission_id}`}
                          >
                            {busyId === s.submission_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <><Check className="w-4 h-4 mr-1" /> Approve</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => reject(s.submission_id)}
                            disabled={busyId === s.submission_id}
                            data-testid={`reject-btn-${s.submission_id}`}
                          >
                            <X className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {status !== "pending" && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => del(s.submission_id)}
                          disabled={busyId === s.submission_id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-btn-${s.submission_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
